import { useState } from 'react'
import { TChatHistory, TChatMessage, IChatCompletionAssistantMessageParam, IChatCompletionToolMessageParam } from 'extra/types/Messages'
import { MessageUtils } from 'extra/MessageUtils'
import { JSONHelper } from 'extra/Helpers/JSONHelper'

type DisplayRole = 'user' | 'assistant' | 'assistant-tool' | 'tool'

function getDisplayRole (msg: TChatMessage): DisplayRole {
  if (MessageUtils.hasToolCall(msg)) return 'assistant-tool'
  if (msg.role === 'tool') return 'tool'
  return msg.role as DisplayRole
}

function generateRandomId (): string {
  return `function-${Math.random().toString(36).substring(2, 10)}`
}

function findNearestToolCallId (history: TChatHistory, currentIndex: number): string {
  for (let i = currentIndex - 1; i >= 0; i--) {
    const msg = history[i]
    if (MessageUtils.hasToolCall(msg)) {
      return msg.tool_calls[0].id
    }
  }
  return generateRandomId()
}

interface ChatHistoryEditorProps {
  history: TChatHistory
  onChange: (history: TChatHistory) => void
  onBlur?: () => void
  defaultRole?: 'user' | 'assistant'
  confirmDelete?: boolean
  minHeight?: string
  mode?: 'basic' | 'full'
}

export function ChatHistoryEditor ({
  history,
  onChange,
  onBlur,
  defaultRole = 'user',
  confirmDelete = false,
  minHeight = 'min-h-[60px]',
  mode = 'full',
}: ChatHistoryEditorProps) {
  const [toolCallsErrors, setToolCallsErrors] = useState<Record<number, string>>({})
  const [toolCallsDrafts, setToolCallsDrafts] = useState<Record<number, string>>({})

  const handleRoleChange = (index: number, displayRole: string) => {
    const newHistory = [...history]
    const currentMsg = newHistory[index]
    const currentDisplayRole = getDisplayRole(currentMsg)

    if (currentDisplayRole === 'assistant-tool' && displayRole !== 'assistant-tool') {
      const updated = { ...currentMsg } as Record<string, unknown>
      delete updated.tool_calls
      newHistory[index] = { ...updated, content: (updated.content as string) ?? '' } as TChatMessage
    }
    if (currentDisplayRole === 'tool' && displayRole !== 'tool') {
      const updated = { ...currentMsg } as Record<string, unknown>
      delete updated.tool_call_id
      delete updated.toolName
      newHistory[index] = { ...updated } as TChatMessage
    }

    if (displayRole === 'assistant-tool') {
      newHistory[index] = {
        role: 'assistant',
        content: null,
        tool_calls: [{
          id: generateRandomId(),
          type: 'function' as const,
          function: {
            name: 'function_name',
            arguments: {},
          },
          extra_content: {
            google: {
              thought_signature: 'skip_thought_signature_validator',
            },
          },
        }],
      } as unknown as TChatMessage

      setToolCallsDrafts((prev) => {
        const next = { ...prev }
        delete next[index]
        return next
      })
      setToolCallsErrors((prev) => {
        const next = { ...prev }
        delete next[index]
        return next
      })
    } else if (displayRole === 'tool') {
      newHistory[index] = {
        ...newHistory[index],
        role: 'tool',
        content: '',
        tool_call_id: findNearestToolCallId(history, index),
      } as TChatMessage
    } else {
      newHistory[index] = { ...newHistory[index], role: displayRole } as TChatMessage
    }

    onChange(newHistory)
  }

  const handleContentChange = (index: number, content: string) => {
    const newHistory = [...history]
    newHistory[index] = { ...newHistory[index], content }
    onChange(newHistory)
  }

  const handleToolCallsChange = (index: number, jsonString: string) => {
    setToolCallsDrafts((prev) => ({ ...prev, [index]: jsonString }))
    setToolCallsErrors((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const handleToolCallsBlur = (index: number) => {
    const draft = toolCallsDrafts[index]
    if (draft === undefined) {
      onBlur?.()
      return
    }

    try {
      const escaped = JSONHelper.escapeNewlinesInsideJsonStrings(draft)
      const parsed = JSON.parse(escaped)
      const newHistory = [...history]
      newHistory[index] = { ...newHistory[index], tool_calls: parsed } as TChatMessage
      onChange(newHistory)
      setToolCallsDrafts((prev) => {
        const next = { ...prev }
        delete next[index]
        return next
      })
      setToolCallsErrors((prev) => {
        const next = { ...prev }
        delete next[index]
        return next
      })
    } catch {
      setToolCallsErrors((prev) => ({ ...prev, [index]: 'Invalid JSON' }))
    }
    onBlur?.()
  }

  const handleToolCallIdChange = (index: number, toolCallId: string) => {
    const newHistory = [...history]
    newHistory[index] = { ...newHistory[index], tool_call_id: toolCallId } as TChatMessage
    onChange(newHistory)
  }

  const handleDelete = (index: number) => {
    if (confirmDelete && !window.confirm('Do you really want to delete this message?')) return
    const newHistory = [...history]
    newHistory.splice(index, 1)
    onChange(newHistory)
  }

  const handleAdd = () => {
    onChange([...history, { role: defaultRole, content: '' }])
  }

  return (
    <div className="flex flex-col gap-3">
      {history.map((msg, index) => {
        const displayRole = getDisplayRole(msg)
        return (
          <div key={index}>
            <div className="shrink-0 py-3 flex gap-2">
              <select
                value={displayRole}
                onChange={(e) => handleRoleChange(index, e.target.value)}
                onBlur={onBlur}
                className="input text-xs"
              >
                <option value="user">user</option>
                <option value="assistant">assistant</option>
                {mode === 'full' && <option value="assistant-tool">assistant - tool call</option>}
                {mode === 'full' && <option value="tool">tool response</option>}
              </select>
              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="text-gray-400 hover:text-red-400 p-1 self-center cursor-pointer"
                title="Remove message"
              >
                ✕
              </button>
            </div>
            <div className="flex-grow flex flex-col gap-2">
              {displayRole === 'assistant-tool' && (
                <>
                  <textarea
                    value={toolCallsDrafts[index] ?? JSONHelper.unescapeNewlinesDeep(JSON.stringify((msg as IChatCompletionAssistantMessageParam).tool_calls, null, 2))}
                    onChange={(e) => handleToolCallsChange(index, e.target.value)}
                    onBlur={() => handleToolCallsBlur(index)}
                    className={`input font-mono text-xs field-sizing-content resize-none overflow-hidden ${minHeight} ${toolCallsErrors[index] ? 'border-red-500' : ''}`}
                    rows={4}
                    placeholder="tool_calls JSON..."
                  />
                  {toolCallsErrors[index] && (
                    <p className="text-red-500 text-xs">{toolCallsErrors[index]}</p>
                  )}
                </>
              )}
              {displayRole === 'tool' && (
                <>
                  <input
                    type="text"
                    value={(msg as IChatCompletionToolMessageParam).tool_call_id || ''}
                    onChange={(e) => handleToolCallIdChange(index, e.target.value)}
                    onBlur={onBlur}
                    className="input text-xs font-mono"
                    placeholder="tool_call_id"
                  />
                  <textarea
                    value={(msg.content as string) || ''}
                    onChange={(e) => handleContentChange(index, e.target.value)}
                    onBlur={onBlur}
                    className={`input font-mono text-xs field-sizing-content resize-none overflow-hidden ${minHeight}`}
                    rows={2}
                    placeholder="Tool response content..."
                  />
                </>
              )}
              {(displayRole === 'user' || displayRole === 'assistant') && (
                <textarea
                  value={(msg.content as string) || ''}
                  onChange={(e) => handleContentChange(index, e.target.value)}
                  onBlur={onBlur}
                  className={`input font-mono text-xs field-sizing-content resize-none overflow-hidden ${minHeight}`}
                  rows={2}
                  placeholder="Message content..."
                />
              )}
            </div>
          </div>
        )
      })}
      <button
        type="button"
        onClick={handleAdd}
        className="btn btn-secondary self-start text-xs"
      >
        + Add Message
      </button>
    </div>
  )
}
